/**
 * useConfirmOnExit
 *
 * Shared hook for the confirm-on-exit column rule flow.
 *
 * When a card is dragged out of a column that requires a field to be filled first,
 * this hook manages the dialog state and the two-step submit:
 *   1. UPDATE_CARD  — fill the required field
 *   2. executeMoveCard — the board-specific card move (optimistic TQ update for
 *                        KanbanBoard, direct call for CohortBoardView, etc.)
 *
 * Usage:
 *   const confirmOnExit = useConfirmOnExit(executeMoveCard);
 *   // trigger when the move is blocked:
 *   confirmOnExit.trigger(card, targetStatusId, fieldConfig, onMoved);
 *   // render:
 *   <ConfirmOnExitDialog open={!!confirmOnExit.state} ... />
 *   <Alert {...confirmOnExit.alertProps} />
 */

import { useState, useCallback } from 'react';
import { actionOrchestrator } from '@/business/orchestrator/ActionOrchestrator';
import { Action } from '@/business/types/action.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import { getResponseCodeMessage } from '@/business/constants/response-codes';
import { useAlert } from '@/components/core/Alert';
import type { ConfirmOnExitConfig } from '@/types/board-config.types';
import type { BoardTask } from '@/types/models/BoardTask';
import type { UpdateTaskInput, UpdateTaskOutput } from '@/types/generated';

interface ConfirmOnExitState {
  card: BoardTask;
  targetStatusId: string;
  fieldConfig: ConfirmOnExitConfig;
  /** Called after the move succeeds — use for optimistic local state updates. */
  onMoved?: () => void;
}

export function useConfirmOnExit(
  /** Board-specific move implementation (optimistic TQ update, local state, etc.) */
  executeMoveCard: (taskId: string, statusId: string) => Promise<void>
) {
  const [state, setState] = useState<ConfirmOnExitState | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const alert = useAlert();

  const trigger = useCallback(
    (
      card: BoardTask,
      targetStatusId: string,
      fieldConfig: ConfirmOnExitConfig,
      onMoved?: () => void
    ) => {
      setState({ card, targetStatusId, fieldConfig, onMoved });
    },
    []
  );

  const dismiss = useCallback(() => setState(null), []);

  const handleSubmit = useCallback(
    async (value: number) => {
      if (!state) return;
      const { card, targetStatusId, fieldConfig, onMoved } = state;

      setIsSubmitting(true);
      try {
        // Step 1: fill the required field on the card
        const updateResult = await actionOrchestrator.execute<
          UpdateTaskInput,
          UpdateTaskOutput
        >(
          Action.UPDATE_TASK,
          { task_id: card.id!, [fieldConfig.field]: value },
          { toast: false }
        );

        const isUpdateSuccess =
          updateResult.code === ResponseCode.SUCCESS ||
          updateResult.code === ResponseCode.CREATED ||
          updateResult.code === ResponseCode.ACCEPTED;

        if (!isUpdateSuccess) {
          alert.show({
            type: 'error',
            title: 'Failed to Update Field',
            message: getResponseCodeMessage(updateResult.code),
            buttons: [{ label: 'Got it', onClick: () => {} }],
          });
          return;
        }

        // Step 2: move the card (board-specific implementation)
        await executeMoveCard(card.id!, targetStatusId);
        onMoved?.();
        setState(null);
      } catch {
        alert.show({
          type: 'error',
          title: 'Error',
          message: 'An unexpected error occurred. Please try again.',
          buttons: [{ label: 'Got it', onClick: () => {} }],
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, executeMoveCard]
  );

  return {
    /** Open the dialog for this card + target status + field config. */
    trigger,
    /** Close without submitting. */
    dismiss,
    /** Non-null when the dialog should be shown. */
    state,
    isSubmitting,
    handleSubmit,
    /** Spread onto <Alert> to display field-update errors. */
    alertProps: alert.props,
  };
}
